import { useEffect, useState } from "react";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { extractPdfStatement, reviewedPdfCsv, type PdfStatementPage } from "./pdf-statement";

/** Banking workflow, not a shared UI primitive. All source material stays in memory. */
export function PdfStatementReview({ file, onPrepared, onCancel }: {
  file: File;
  onPrepared: (csv: string) => void;
  onCancel: () => void;
}) {
  const [pages, setPages] = useState<PdfStatementPage[]>([]);
  const [progress, setProgress] = useState("Opening PDF on this device…");
  const [error, setError] = useState<string | null>(null);
  const [csv, setCsv] = useState("date,description,amount\n");
  const [reviewed, setReviewed] = useState(false);
  const [forceOcr, setForceOcr] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setPages([]);
    setReviewed(false);
    setError(null);
    setProgress("Opening PDF on this device…");
    void extractPdfStatement(file, controller.signal, message => { if (active) setProgress(message); }, forceOcr)
      .then(result => { if (active) { setPages(result); setProgress(""); } })
      .catch(cause => { if (active) { setError(cause instanceof Error ? cause.message : "Could not read the PDF. Nothing was imported."); setProgress(""); } });
    return () => { active = false; controller.abort(); };
  }, [file, forceOcr]);
  return <section className="grid gap-3" aria-label="Review PDF statement">
    <p className="text-sm">PDF review · {file.name}. Processing stays on this device. English OCR is used for scanned pages. No transactions are inferred or saved automatically.</p>
    {progress && <p role="status">{progress}</p>}
    {error && <p role="alert" className="text-[var(--danger)]">{error}</p>}
    {!progress && <label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={forceOcr} onChange={event => setForceOcr(event.target.checked)} />Use OCR on every page (for mixed scans or incomplete embedded text)</label>}
    {pages.map(page => <details key={page.page} className="rounded border border-[var(--border)] p-3">
      <summary>Page {page.page} · {page.method === "ocr" ? `OCR${page.confidence === undefined ? "" : ` (${Math.round(page.confidence)}% confidence)`}` : "Embedded text"}</summary>
      <img src={page.image} alt={`Original statement page ${page.page}`} className="mt-2 w-full" />
      <pre className="mt-2 max-h-72 overflow-auto whitespace-pre-wrap text-xs">{page.text || "No text recognized. Enter this page’s transactions manually."}</pre>
    </details>)}
    {pages.length > 0 && <>
      <label htmlFor="pdf-reviewed-rows" className="grid gap-1 text-sm">
        Reviewed transaction rows (CSV)
        <textarea id="pdf-reviewed-rows" className="input min-h-40 font-mono text-xs" value={csv}
          onChange={event => { setCsv(event.target.value); setReviewed(false); }} />
      </label>
      <p className="text-xs">Use YYYY-MM-DD dates and signed decimal amounts in this bank account’s currency: money in positive, money out negative. Quote descriptions containing commas. Exclude balance totals, headers and footers. Copy and verify every transaction from all pages; extraction can misread or omit data.</p>
      <label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={reviewed} onChange={event => setReviewed(event.target.checked)} />I checked every page, transaction, date, amount and debit/credit sign against the original statement.</label>
      <Button type="button" disabled={!reviewed} onClick={() => {
        try { onPrepared(reviewedPdfCsv(csv, reviewed)); }
        catch (cause) { setError(cause instanceof Error ? cause.message : "Review the transaction rows."); }
      }}>Use reviewed rows</Button>
    </>}
    <Button type="button" variant="secondary" onClick={onCancel}>Cancel PDF review</Button>
  </section>;
}
